import {
  Button,
  Grid,
  InputLabel,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import InfoIcon from '@material-ui/icons/Info';
import LinkOffIcon from '@material-ui/icons/LinkOff';
import { Skeleton } from '@material-ui/lab';
import { cloneDeep, sumBy } from 'lodash-es';
import React, { ReactElement, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';

import { IAdserverGlobalVast } from '~/src/dash/api/adserver-global-vast';
import {
  EAdserverCreativeType,
  EBannerRank,
  IAdserverCreative,
  IAdserverCreativeFlightDatesSubgroup,
  IAdserverGlobalMediafile,
  IVariantMediafile,
} from '~/src/dash/api/console-api';
import { EBool } from '~/src/dash/api/types/bool';
import { CopyButton } from '~/src/dash/components/button';
import {
  DateTimeRangeComponent,
  IDateRangeChangeEvent,
} from '~/src/dash/components/datepicker/date-time-range-component';
import { ISelectOption } from '~/src/dash/components/dropdowns/common';
import { IndependentSelect } from '~/src/dash/components/dropdowns/IndependentSelect';
import { IconV2 } from '~/src/dash/components/icons/IconV2';
import { MenuButton } from '~/src/dash/components/menu-button';
import { ModelSecondaryButton } from '~/src/dash/components/model-secondary-button';
import { ModernTextField } from '~/src/dash/components/modern-text-field';
import { CreativeTypeMediafile, CreativeTypeVast } from '~/src/dash/globals/constants';
import { borderRadius, Text, Textarea, TextInput } from '~/src/dash/publicaUI';
import { IUserState } from '~/src/dash/reducers/user-reducers';
import { IMenuList } from '~/src/dash/types/menuList';
import { ChipAdserverCreativeBannerTier } from '~/src/dash/utils/adserver-creative-type';
import { publicaColors } from '~/src/dash/utils/colors';
import { formatDateFromUTCToOffset, formatDateWithOffsetToUTC } from '~/src/dash/utils/date';
import { bigRadius, deleteColor } from '~/src/dash/utils/material-dashboard-react';
import {
  obfuscateAdserverCreative,
  obfuscateGlobalMediafile,
  obfuscateVariant,
} from '~/src/dash/utils/obfuscate';
import { IPermissionCheck } from '~/src/dash/utils/permission_service';
import { hasRoleSuperUser } from '~/src/dash/utils/roles';
import { isBannerType } from '~/src/dash/views/campaign/common';

interface ICreativeFlightDatesSubgroup {
  adserverCampaignID: string;
  blankMediaFileList: IMenuList;
  blankVastList: IMenuList;
  campaignID: string;
  classes: { [key: string]: string };
  creativeType: string;
  defaultStartDate: string;
  defaultEndDate: string;
  forceMenuButtonClosed: boolean;
  globalMediaFiles: IAdserverGlobalMediafile[];
  handleChangeVariantActive: (variantIdx: number, gmfIdx: number) => void;
  handleOpenCreateCheckpointModal: (creativeID: string) => void;
  handleVastEditEnabled: (index: number, vast?: IAdserverGlobalVast) => void;
  isLoadingMediaFilesAndVasts: boolean;
  isNativeVastAuthorized: boolean;
  onSubgroupChange: (subgroup: IAdserverCreativeFlightDatesSubgroup, subgroupIdx: number) => void;
  mediaFileOptions: ISelectOption[];
  removeGlobalMediafile: (creativeIdx: number, mediafileID: string) => void;
  removeSubgroup: () => void;
  renderCreateBannerFromAssetURLRow: () => ReactElement;
  renderCreateFromAssetURLRow: (
    cidx: number,
    GlobalMediaFiles: IAdserverGlobalMediafile[],
    subgroupIdx?: number, // if EnableFlightDateSubgroups is true, then we need the subgroup index for AdserverCreativeFlightDatesSubgroups.
  ) => ReactElement;
  renderMenuTitle: (inMenu: boolean, disabled: boolean) => ReactElement;
  renderUploadBannerRow: (
    cidx: number,
    GlobalMediaFiles: IAdserverGlobalMediafile[],
    subgroupIdx?: number, // if EnableFlightDateSubgroups is true, then we need the subgroup index for AdserverCreativeFlightDatesSubgroups.
  ) => ReactElement;
  renderUploadRow: (
    cidx: number,
    GlobalMediaFiles: IAdserverGlobalMediafile[],
    subgroupIdx?: number, // if EnableFlightDateSubgroups is true, then we need the subgroup index for AdserverCreativeFlightDatesSubgroups.
  ) => ReactElement;
  setState: (state: Record<string, any>) => void;
  subgroup: IAdserverCreativeFlightDatesSubgroup;
  subgroupIdx: number;
  tempVastURL: string;
  timezone: string;
  user: IUserState;
  vastEditIndex: number;
  vastOptions: ISelectOption[];
  vasts: IAdserverGlobalVast[];
  writeAuthorization: IPermissionCheck;
}

export const CreativeSubgroup = ({
  adserverCampaignID,
  blankMediaFileList,
  blankVastList,
  campaignID,
  classes,
  creativeType,
  defaultStartDate,
  defaultEndDate,
  forceMenuButtonClosed,
  globalMediaFiles,
  handleChangeVariantActive,
  handleOpenCreateCheckpointModal,
  handleVastEditEnabled,
  isLoadingMediaFilesAndVasts,
  isNativeVastAuthorized,
  onSubgroupChange,
  mediaFileOptions,
  removeGlobalMediafile,
  removeSubgroup,
  renderCreateBannerFromAssetURLRow,
  renderCreateFromAssetURLRow,
  renderMenuTitle,
  renderUploadBannerRow,
  renderUploadRow,
  setState,
  subgroup,
  subgroupIdx,
  tempVastURL,
  timezone,
  user,
  vastEditIndex,
  vastOptions,
  vasts,
  writeAuthorization,
}: ICreativeFlightDatesSubgroup) => {
  const [reactKey, setReactKey] = useState(0);
  const forceUpdate = () => {
    setReactKey((value) => value + 1);
  };

  const renderSubgroupVast = (creative: IAdserverCreative, creativeIdx: number) => {
    const vast = vasts.find((v: IAdserverGlobalVast) => v.ID === creative.AdserverGlobalVastID);
    const vastEditEnabled = vastEditIndex === creativeIdx;

    return (
      <Grid container direction='column'>
        {/* vast selector */}
        <Grid item className={classes.creativeRow}>
          <Grid container alignItems='center' spacing={2} style={{ marginBottom: 10 }}>
            <Grid item xs={2}>
              <Typography
                style={{
                  fontSize: 14,
                  fontWeight: 500,
                }}
              >
                Select Existing Vast
              </Typography>
            </Grid>
            <Grid item xs={8}>
              {isLoadingMediaFilesAndVasts ? (
                <Skeleton
                  variant='rect'
                  animation='wave'
                  width={480}
                  height={28}
                  style={{ borderRadius: borderRadius.base }}
                />
              ) : (
                <IndependentSelect
                  isDisabled={!writeAuthorization.isAuthorized}
                  key={creative.ID}
                  enableLabelTooltip
                  handleApplySelect={(ids: Set<string>) => {
                    forceUpdate();
                    const newSubgroup = cloneDeep(subgroup);
                    newSubgroup.AdserverCreatives[creativeIdx].AdserverGlobalVastID = parseInt(
                      [...ids][0],
                      10,
                    );
                    onSubgroupChange(newSubgroup, subgroupIdx);
                  }}
                  selectedLimit={1}
                  listLabel='Vasts'
                  options={vastOptions}
                  placeholder='Select Existing Vast'
                  selectedOptionIDs={
                    creative.AdserverGlobalVastID
                      ? new Set([creative.AdserverGlobalVastID])
                      : new Set()
                  }
                  singularLabel='Vast'
                  maxWidth={480}
                />
              )}
            </Grid>
          </Grid>
          {vast && (
            <Grid container alignItems='center' spacing={2}>
              <Grid item xs={2}>
                {vastEditEnabled ? (
                  <Grid container direction='row' alignItems='center' justify='flex-start'>
                    <Grid item>
                      <ModelSecondaryButton
                        onClick={() => handleVastEditEnabled(creativeIdx, vast)}
                      >
                        SAVE
                      </ModelSecondaryButton>
                    </Grid>
                    <Grid item>
                      <ModelSecondaryButton onClick={() => handleVastEditEnabled(-1, vast)}>
                        CANCEL
                      </ModelSecondaryButton>
                    </Grid>
                  </Grid>
                ) : (
                  <Grid container direction='row' alignItems='center' justify='flex-start'>
                    <Grid item>
                      <ModelSecondaryButton
                        disabled={!writeAuthorization.isAuthorized}
                        onClick={() => handleVastEditEnabled(creativeIdx, vast)}
                      >
                        EDIT VAST URL
                      </ModelSecondaryButton>
                    </Grid>
                  </Grid>
                )}
              </Grid>
              <Grid item xs={6}>
                <Textarea
                  key={reactKey}
                  msDelay={800}
                  isDisabled={!vastEditEnabled}
                  initialValue={vastEditEnabled ? tempVastURL : vast ? vast.URL : ''}
                  handleOnChange={(value: string) => {
                    setState({ tempVastURL: value });
                  }}
                  style={{ minWidth: '480px' }}
                />
              </Grid>
            </Grid>
          )}
        </Grid>
        {/* add vast */}
        <Grid item className={classes.creativeRow} style={{ marginBottom: 10 }}>
          <Grid container direction='row' alignItems='center' spacing={2}>
            <Grid item>
              <AddIcon
                style={{
                  marginTop: 5,
                  color: 'black',
                }}
              />
            </Grid>
            <Grid
              item
              onClick={() => {
                if (writeAuthorization.isAuthorized) {
                  handleOpenCreateCheckpointModal(creative.ID);
                }
              }}
            >
              <Typography
                className={classes.menuButtonText}
                style={{
                  cursor: 'pointer',
                  color: writeAuthorization.isAuthorized
                    ? publicaColors.black
                    : publicaColors.lightGray,
                }}
              >
                ADD NEW VAST
              </Typography>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    );
  };

  const renderSubgroupMediafileMenuButton = (
    creativeIdx: number,
    GlobalMediaFiles: IAdserverGlobalMediafile[],
  ) => {
    const disabled = !writeAuthorization.isAuthorized || GlobalMediaFiles.length >= 1;
    const menuRow = {
      closeOnClick: true,
      render: () => renderMenuTitle(true, disabled),
    };
    const createFromAssetURLRow = {
      closeOnClick: true,
      render: () => renderCreateFromAssetURLRow(creativeIdx, GlobalMediaFiles, subgroupIdx),
    };
    const bannerFromAssetURLRow = {
      closeOnClick: true,
      render: renderCreateBannerFromAssetURLRow,
    };
    const uploadRow = {
      closeOnClick: false,
      render: () => renderUploadRow(creativeIdx, GlobalMediaFiles, subgroupIdx),
    };
    const uploadBannerRow = {
      closeOnClick: false,
      render: () => renderUploadBannerRow(creativeIdx, GlobalMediaFiles, subgroupIdx),
    };
    const menuButtonItems = [menuRow];
    if (isNativeVastAuthorized) {
      menuButtonItems.push(bannerFromAssetURLRow, uploadBannerRow);
    }
    menuButtonItems.push(createFromAssetURLRow, uploadRow);

    return (
      <Grid onClick={() => setState({ forceMenuButtonClosed: false })}>
        <MenuButton
          forceClosed={forceMenuButtonClosed}
          disabled={disabled}
          disabledMsg='Only one asset may be assigned to each creative. Remove the current asset if you want to add a new asset.'
          titleRender={() => renderMenuTitle(false, disabled)}
          items={menuButtonItems}
          minWidth={250}
          enableGreyFirstItem={true}
          enableDividers={true}
        />
      </Grid>
    );
  };

  const renderSubgroupMediafiles = (creative: IAdserverCreative, creativeIdx: number) => {
    const creativeGlobalMediaFiles: IAdserverGlobalMediafile[] = [];
    let gmfIdx = -1;
    for (const id of creative.AdserverGlobalMediafileIDs || []) {
      for (let idx = 0; idx < globalMediaFiles.length; idx++) {
        const mediaFile = globalMediaFiles[idx];
        if (mediaFile.ID === id) {
          gmfIdx = idx;
          creativeGlobalMediaFiles.push(mediaFile);
        }
      }
    }

    return (
      <Grid
        container
        spacing={2}
        direction='column'
        style={{
          width: '100%',
          paddingBottom: 10,
        }}
      >
        <Grid item style={{ marginLeft: -8 }}>
          {renderSubgroupMediafileMenuButton(creativeIdx, creativeGlobalMediaFiles)}
        </Grid>
        <Grid item>
          <Grid container direction='row' alignItems='center' spacing={2}>
            <Grid item xs={2}>
              <Typography className={classes.menuButtonText}>LINK EXISTING ASSET</Typography>
            </Grid>
            <Grid item xs={8}>
              {isLoadingMediaFilesAndVasts ? (
                <Skeleton
                  variant='rect'
                  animation='wave'
                  width={480}
                  height={28}
                  style={{ borderRadius: borderRadius.base }}
                />
              ) : (
                <IndependentSelect
                  key={creative.ID}
                  enableLabelTooltip
                  handleApplySelect={(ids: Set<string>) => {
                    const newSubgroup = cloneDeep(subgroup);
                    if (creativeType === CreativeTypeMediafile && ids.size > 0) {
                      newSubgroup.AdserverCreatives[creativeIdx].AdserverGlobalMediafileIDs = [
                        ...ids,
                      ];
                    }
                    if (creativeType !== CreativeTypeMediafile && ids.size > 0) {
                      newSubgroup.AdserverCreatives[creativeIdx].AdserverGlobalVastID = parseInt(
                        [...ids][0],
                        10,
                      );
                    }
                    onSubgroupChange(newSubgroup, subgroupIdx);
                  }}
                  selectedLimit={1}
                  listLabel='Assets'
                  options={mediaFileOptions}
                  placeholder='Select Existing Asset'
                  selectedOptionIDs={new Set(creative.AdserverGlobalMediafileIDs)}
                  singularLabel='Asset'
                  maxWidth={480}
                />
              )}
            </Grid>
          </Grid>
        </Grid>
        {/* asset / variant table */}
        {creativeGlobalMediaFiles.length > 0 && (
          <Grid item>
            <Table>
              <TableHead
                style={{
                  backgroundColor: publicaColors.white,
                }}
              >
                <TableRow>
                  {hasRoleSuperUser(user.user) && (
                    <TableCell
                      align='right'
                      padding='none'
                      style={{
                        textAlign: 'center',
                      }}
                    >
                      ID *
                    </TableCell>
                  )}
                  <TableCell
                    padding='none'
                    style={{
                      color: publicaColors.gray,
                    }}
                  >
                    Asset Name
                  </TableCell>
                  <TableCell
                    align='right'
                    style={{
                      color: publicaColors.gray,
                      textAlign: 'center',
                    }}
                    padding='none'
                  >
                    URL
                  </TableCell>
                  <TableCell
                    align='right'
                    style={{
                      color: publicaColors.gray,
                      textAlign: 'center',
                    }}
                    padding='none'
                  >
                    Bitrate (kbps)
                  </TableCell>
                  <TableCell
                    align='right'
                    style={{
                      color: publicaColors.gray,
                      textAlign: 'center',
                    }}
                    padding='none'
                  >
                    Duration (secs)
                  </TableCell>
                  <TableCell
                    align='right'
                    style={{
                      color: publicaColors.gray,
                      textAlign: 'center',
                    }}
                    padding='none'
                  >
                    Resolution
                  </TableCell>
                  <TableCell
                    padding='none'
                    style={{
                      textAlign: 'center',
                      color: publicaColors.gray,
                    }}
                    align='right'
                  >
                    Remove / Active
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {
                  // Global mediafile
                  creativeGlobalMediaFiles.map((mediafile: IAdserverGlobalMediafile) => {
                    // shorten the URL and shorten the bit rate to make it more readable
                    const Duration = mediafile.Duration;
                    let Bitrate = String(mediafile.Bitrate);
                    const bitRateNum = parseInt(mediafile.Bitrate, 10) || 0;
                    if (bitRateNum > 10000) {
                      Bitrate = Math.floor(bitRateNum / 1000) + ' kbps';
                    }
                    // truncate the name and url if the value is too long
                    const mediafileName = obfuscateGlobalMediafile(
                      mediafile,
                      user.user.Username,
                      '',
                      mediafile.Name,
                    );
                    const truncatedMediafileName =
                      mediafileName.length <= 30
                        ? mediafileName
                        : mediafileName.slice(0, 30) + '...';
                    const mediafileURL = obfuscateGlobalMediafile(
                      mediafile,
                      user.user.Username,
                      'url',
                      mediafile.URL,
                    );
                    const truncatedMediafileURL =
                      mediafileURL.length <= 30 ? mediafileURL : mediafileURL.slice(0, 30) + '...';
                    return (
                      <TableRow key={mediafile.ID}>
                        {/* ID (Super user only) */}
                        {hasRoleSuperUser(user.user) && (
                          <TableCell
                            style={{
                              textAlign: 'center',
                            }}
                            padding='none'
                            align='right'
                          >
                            <CopyButton
                              text={mediafile.ID ? mediafile.ID + ' *' : '*'}
                              copy={mediafile.ID || ''}
                            />
                          </TableCell>
                        )}
                        {/* Name Link */}
                        <TableCell padding='none'>
                          <Tooltip title={mediafileName}>
                            <a
                              href={mediafile.URL}
                              style={{
                                color: 'black',
                              }}
                              target='_blank'
                            >
                              {truncatedMediafileName}
                            </a>
                          </Tooltip>
                        </TableCell>
                        {/* URL */}
                        <TableCell
                          padding='none'
                          align='right'
                          style={{
                            textAlign: 'center',
                          }}
                        >
                          <Tooltip title={mediafileURL}>
                            <Typography style={{ fontSize: 14 }}>
                              {truncatedMediafileURL}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        {/* Bit Rate */}
                        <TableCell
                          padding='none'
                          align='right'
                          style={{
                            textAlign: 'center',
                          }}
                        >
                          <Typography style={{ fontSize: 14 }}>{Bitrate}</Typography>
                        </TableCell>
                        {/* Duration */}
                        <TableCell
                          padding='none'
                          align='right'
                          style={{
                            textAlign: 'center',
                          }}
                        >
                          <Typography style={{ fontSize: 14 }}>{Duration}</Typography>
                        </TableCell>
                        {/* Resolution */}
                        <TableCell
                          padding='none'
                          align='right'
                          style={{
                            textAlign: 'center',
                          }}
                        >
                          <Typography style={{ fontSize: 14 }}>
                            {`${mediafile.Width} x ${mediafile.Height}`}
                          </Typography>
                        </TableCell>
                        {/* Remove asset from creative */}
                        <TableCell
                          style={{
                            textAlign: 'center',
                          }}
                          padding='none'
                          align='right'
                        >
                          <Button
                            disabled={!writeAuthorization.isAuthorized}
                            onClick={() => {
                              removeGlobalMediafile(creativeIdx, mediafile.ID);
                            }}
                          >
                            <LinkOffIcon
                              style={{
                                fill: deleteColor,
                              }}
                            />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })
                }
                {creativeGlobalMediaFiles.length === 1 &&
                  // Variants
                  creativeGlobalMediaFiles[0].VariantMediafiles.map(
                    (variant: IVariantMediafile, vIdx: number) => {
                      // shorten the URL and shorten the bit rate to make it more readable
                      let Bitrate = String(variant.Bitrate);
                      const bitRateNum = variant.Bitrate;
                      if (bitRateNum > 10000) {
                        Bitrate = Math.floor(bitRateNum / 1000) + ' kbps';
                      }
                      // truncate the name and url if the value is too long
                      const variantName = obfuscateVariant(
                        variant,
                        user.user.Username,
                        '',
                        variant.Name,
                      );
                      const truncatedVariantName =
                        variantName.length <= 30 ? variantName : variantName.slice(0, 30) + '...';
                      const variantURL = obfuscateVariant(
                        variant,
                        user.user.Username,
                        'url',
                        variant.URL,
                      );
                      const truncatedVariantURL =
                        variantURL.length <= 30 ? variantURL : variantURL.slice(0, 30) + '...';
                      return (
                        <TableRow>
                          {/* ID (Super user only) */}
                          {hasRoleSuperUser(user.user) && (
                            <TableCell
                              style={{
                                textAlign: 'center',
                              }}
                              padding='none'
                              align='right'
                            >
                              <CopyButton
                                text={String(variant.ID) ? String(variant.ID) + ' *' : '*'}
                                copy={String(variant.ID) || ''}
                              />
                            </TableCell>
                          )}
                          {/* Name Link */}
                          <TableCell padding='none'>
                            <Tooltip title={variantName}>
                              <a
                                href={variant.URL}
                                style={{
                                  color: 'black',
                                }}
                                target='_blank'
                              >
                                {truncatedVariantName}
                              </a>
                            </Tooltip>
                          </TableCell>
                          {/* URL */}
                          <TableCell
                            padding='none'
                            align='right'
                            style={{
                              textAlign: 'center',
                            }}
                          >
                            <Tooltip title={variantURL}>
                              <Typography
                                style={{
                                  fontSize: 14,
                                }}
                              >
                                {truncatedVariantURL}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                          {/* Bit Rate */}
                          <TableCell
                            padding='none'
                            align='right'
                            style={{
                              textAlign: 'center',
                            }}
                          >
                            <Typography style={{ fontSize: 14 }}>{Bitrate}</Typography>
                          </TableCell>
                          {/* Duration */}
                          <TableCell
                            padding='none'
                            align='right'
                            style={{
                              textAlign: 'center',
                            }}
                          >
                            <Typography style={{ fontSize: 14 }}>{variant.Duration}</Typography>
                          </TableCell>
                          {/* Resolution */}
                          <TableCell
                            padding='none'
                            align='right'
                            style={{
                              textAlign: 'center',
                            }}
                          >
                            <Typography style={{ fontSize: 14 }}>
                              {`${variant.Width} x ${variant.Height}`}
                            </Typography>
                          </TableCell>
                          {/* Remove asset from creative */}
                          <TableCell
                            style={{
                              textAlign: 'center',
                            }}
                            padding='none'
                            align='center'
                          >
                            <Switch
                              disabled={!writeAuthorization.isAuthorized}
                              className={classes.switch}
                              checked={variant.Active === EBool.EBool_TRUE}
                              name='Active'
                              onChange={() => handleChangeVariantActive(vIdx, gmfIdx)}
                              color='secondary'
                            />
                          </TableCell>
                        </TableRow>
                      );
                    },
                  )}
              </TableBody>
            </Table>
          </Grid>
        )}
      </Grid>
    );
  };

  const handleFlightDatesSubgroupDateChange = ({ start, end }: Partial<IDateRangeChangeEvent>) => {
    if (start && end && start.isAfter(end) && end.year() !== 0) {
      setState({
        snackbarError: true,
        snackbarMessage: 'Creative start date must be before creative end date.',
      });
      return;
    }
    const newSubgroup = cloneDeep(subgroup);
    newSubgroup.StartDate =
      start !== undefined ? formatDateWithOffsetToUTC(start, timezone) : 'null';
    newSubgroup.EndDate = end !== undefined ? formatDateWithOffsetToUTC(end, timezone) : 'null';
    newSubgroup.AdserverCreatives.forEach((creative) => {
      creative.StartDate = newSubgroup.StartDate;
      creative.EndDate = newSubgroup.EndDate;
    });
    onSubgroupChange(newSubgroup, subgroupIdx);
  };

  const addNewSubgroupCreative = () => {
    const newSubgroup = cloneDeep(subgroup);
    newSubgroup.AdserverCreatives.push({
      ID: '0',
      UUID: '',
      CreatedAt: '',
      UpdatedAt: '',
      DeletedAt: '',
      StartDate: subgroup.StartDate,
      EndDate: subgroup.EndDate,
      Name: 'Creative ' + String(Date.now()),
      Weight: '1',
      VAST: '',
      AdserverCampaignID: adserverCampaignID,
      AdserverGlobalMediafileIDs: [],
      _CreativeType: CreativeTypeVast,
      CreativeType: EAdserverCreativeType.Video,
      BannerRank: EBannerRank.NotBanner,
      GlobalMediafileList: cloneDeep(blankMediaFileList),
      GlobalVastList: cloneDeep(blankVastList),
    });
    newSubgroup.TotalWeight = sumBy(
      newSubgroup.AdserverCreatives,
      (v) => parseInt(v.Weight, 10) || 0,
    );
    onSubgroupChange(newSubgroup, subgroupIdx);
  };

  const evenCheckpointWeights = () => {
    const newSubgroup = cloneDeep(subgroup);
    const creatives = newSubgroup.AdserverCreatives;
    if (creatives?.length < 1) return;

    const quotient = Math.floor(100 / creatives.length);
    let remainder = 100 % creatives.length;
    let totalWeight = 0;
    for (const creative of creatives) {
      creative.Weight = String(quotient + remainder);
      remainder = 0;
      totalWeight += Number(creative.Weight);
    }
    newSubgroup.TotalWeight = totalWeight;
    unstable_batchedUpdates(() => {
      forceUpdate();
      onSubgroupChange(newSubgroup, subgroupIdx);
    });
  };

  const clearSubgroupDates = () => {
    const newSubgroup = cloneDeep(subgroup);
    newSubgroup.StartDate = defaultStartDate;
    newSubgroup.EndDate = defaultEndDate;
    onSubgroupChange(newSubgroup, subgroupIdx);
  };

  const removeSubgroupCreative = (creativeIdx: number) => {
    const newSubgroup = cloneDeep(subgroup);
    newSubgroup.AdserverCreatives.splice(creativeIdx, 1);
    onSubgroupChange(newSubgroup, subgroupIdx);
  };

  return (
    <Grid
      container
      style={{
        background: '#FFFFFF 0% 0% no-repeat padding-box',
        boxShadow: '3px 3px 6px #CBCBCB',
        opacity: '5',
        marginBottom: '10px',
      }}
    >
      <Grid item className={classes.creativeRow}>
        <Grid container alignItems='center' spacing={2}>
          <Grid item xs={2}>
            <Typography
              style={{
                fontSize: 14,
                fontWeight: 500,
              }}
            >
              Checkpoint {subgroupIdx + 1}
            </Typography>
          </Grid>
          <Grid item xs={6} className={`creative-date-${subgroupIdx}`}>
            <DateTimeRangeComponent
              enableClearSelection={true}
              disabled={!writeAuthorization.isAuthorized}
              timezone={timezone}
              start={
                subgroup.StartDate && subgroup.StartDate !== 'null'
                  ? formatDateFromUTCToOffset(subgroup.StartDate, timezone)
                  : undefined
              }
              end={
                subgroup.EndDate && subgroup.EndDate !== 'null'
                  ? formatDateFromUTCToOffset(subgroup.EndDate, timezone)
                  : undefined
              }
              onChange={(e) => handleFlightDatesSubgroupDateChange(e)}
              openDirection={'up'}
              enableSelectAll={!!campaignID}
              enablePresets={true}
            />
          </Grid>
          <Grid item xs={2}>
            <ModelSecondaryButton
              disabled={!writeAuthorization.isAuthorized}
              style={{
                paddingTop: '18px',
                paddingBottom: '18px',
              }}
              onClick={clearSubgroupDates}
            >
              <Typography
                noWrap
                style={{
                  textTransform: 'uppercase',
                  fontSize: 14,
                  fontWeight: 500,
                  marginLeft: 5,
                }}
              >
                Clear Dates
              </Typography>
            </ModelSecondaryButton>
          </Grid>
          <Grid item xs={2}>
            <Grid container justify='flex-end' direction='row' alignItems='center'>
              <Button onClick={() => removeSubgroup()} disabled={!writeAuthorization.isAuthorized}>
                <DeleteIcon
                  style={{
                    cursor: 'pointer',
                    fill: deleteColor,
                  }}
                />
              </Button>
            </Grid>
          </Grid>
        </Grid>
        <Grid container direction='row' alignItems='center' spacing={2}>
          <Grid item xs={2}>
            <InputLabel
              style={{
                marginRight: '20px',
                fontSize: '14px',
                color: 'black',
              }}
            >
              <IconV2
                name='hamburger'
                color={publicaColors.black}
                style={{ width: '12px', height: '12px', marginRight: '5px' }}
              />
              Total Creative Weight
            </InputLabel>
          </Grid>
          <Grid item xs={1}>
            <Typography
              className={
                Number(subgroup.TotalWeight) === 100
                  ? `${classes.boldText} pb-campaign-form-view_total_weight`
                  : classes.boldTextError
              }
            >
              {subgroup.TotalWeight}%
            </Typography>
          </Grid>
          <Grid item xs={2}>
            <ModelSecondaryButton
              onClick={evenCheckpointWeights}
              disabled={!writeAuthorization.isAuthorized}
            >
              Even Weights
            </ModelSecondaryButton>
          </Grid>
        </Grid>
        {subgroup.errors !== '' && (
          <Grid item>
            <Grid container direction='row' alignItems='flex-start' justify='space-between'>
              <Grid item>
                <Typography style={{ fontSize: 11, color: 'red' }}>{subgroup.errors}</Typography>
              </Grid>
            </Grid>
          </Grid>
        )}
      </Grid>

      {/* adserver creatives */}
      {subgroup.AdserverCreatives.map((creative: IAdserverCreative, creativeIdx: number) => {
        const obfuscatedCreativeName = obfuscateAdserverCreative(
          creative,
          user.user.Username,
          creative.Name,
        );
        return (
          <Grid
            key={creative.ID === '0' ? creativeIdx : creative.ID} // new creative does not have unique id to be used as key
            container
            style={{
              background: '#F8FAFF 0% 0% no-repeat padding-box',
              boxShadow: '0px 3px 6px #CBCBCB',
              margin: '20px',
              borderRadius: bigRadius,
              opacity: '1',
              marginBottom: '10px',
            }}
          >
            {/* creative name, id and delete */}
            <Grid item className={classes.creativeRow}>
              <Grid container direction='row' justify='flex-start' alignItems='center'>
                <Grid item xs={2} style={{ marginLeft: -10 }}>
                  <ModernTextField
                    value={obfuscatedCreativeName}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      const newSubgroup = cloneDeep(subgroup);
                      newSubgroup.AdserverCreatives[creativeIdx].Name = event.target.value;
                      onSubgroupChange(newSubgroup, subgroupIdx);
                    }}
                    disabled={!writeAuthorization.isAuthorized}
                    disableAllUppercase={true}
                  />
                </Grid>
                <Grid item xs={9}>
                  <Grid
                    container
                    direction='row'
                    alignItems='center'
                    justifyContent='space-between'
                  >
                    <Grid item>
                      <Grid container direction='row' alignItems='center'>
                        <Grid item>
                          <CopyButton
                            text={'Creative ID: ' + (creative.ID === '' ? 'New' : creative.ID)}
                            copy={creative.ID}
                          />
                        </Grid>
                        {hasRoleSuperUser(user.user) && !!creative.ID && (
                          <Grid item>
                            <CopyButton
                              text={
                                'Creative UUID *: ' + (creative.UUID === '' ? 'New' : creative.UUID)
                              }
                              copy={creative.UUID}
                            />
                          </Grid>
                        )}
                      </Grid>
                    </Grid>
                    {creative?.CreativeType === EAdserverCreativeType.Banner && (
                      <ChipAdserverCreativeBannerTier
                        label={
                          creative?.BannerRank === EBannerRank.Primary
                            ? 'Primary Banner'
                            : 'Secondary Banner'
                        }
                      />
                    )}
                  </Grid>
                </Grid>
                <Grid
                  item
                  xs={1}
                  spacing={6}
                  className='pb-campaign-form-view_creative_delete_button'
                >
                  <Grid container justify='flex-end' direction='row' alignItems='center'>
                    <Button
                      onClick={() => removeSubgroupCreative(creativeIdx)}
                      disabled={!writeAuthorization.isAuthorized}
                    >
                      <DeleteIcon
                        style={{
                          cursor: 'pointer',
                          fill: deleteColor,
                        }}
                      />
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            {/* creative weight, only for non-banner */}
            {!isBannerType(creative) && (
              <Grid item className={classes.creativeRow}>
                <Grid container alignItems='center' spacing={2}>
                  <Grid item xs={2}>
                    <Text bold small>
                      Weight
                      <Tooltip
                        title={
                          'Creative weight must be a number 1-100 which represents a percentage of all impressions this creative will receive. Note that Companion Ads are exempt from this.'
                        }
                      >
                        <InfoIcon
                          style={{
                            verticalAlign: 'bottom',
                            width: '1rem',
                            color: publicaColors.mediumLightGray,
                            marginLeft: '5px',
                          }}
                        />
                      </Tooltip>
                    </Text>
                  </Grid>
                  <Grid item xs={1}>
                    <TextInput
                      key={reactKey}
                      inputSize='small'
                      type='number'
                      max={100}
                      min={1}
                      msDelay={800}
                      initialValue={creative.Weight}
                      className='pb-campaign-form-view_creative_weight_button'
                      handleOnChange={(value: string) => {
                        const newSubgroup = cloneDeep(subgroup);
                        newSubgroup.AdserverCreatives[creativeIdx].Weight = value;
                        newSubgroup.TotalWeight = sumBy(
                          newSubgroup.AdserverCreatives,
                          (v) => parseInt(v.Weight, 10) || 0,
                        );
                        onSubgroupChange(newSubgroup, subgroupIdx);
                      }}
                      isDisabled={!writeAuthorization.isAuthorized}
                    />
                  </Grid>
                </Grid>
              </Grid>
            )}
            <Grid item className={classes.creativeRow}>
              <Grid container alignItems='center' spacing={2}>
                <Grid item xs={2}>
                  <Text bold small>
                    ClickThrough URL
                  </Text>
                </Grid>
                <Grid item xs={1}>
                  <TextInput
                    key={reactKey}
                    inputSize='small'
                    msDelay={800}
                    initialValue={creative.ClickThroughURL}
                    handleOnChange={(value: string) => {
                      const newSubgroup = cloneDeep(subgroup);
                      newSubgroup.AdserverCreatives[creativeIdx].ClickThroughURL = value;
                      onSubgroupChange(newSubgroup, subgroupIdx);
                    }}
                    inputStyle={{
                      minWidth: '480px',
                    }}
                    isDisabled={!writeAuthorization.isAuthorized}
                  />
                </Grid>
              </Grid>
            </Grid>

            {creativeType === CreativeTypeVast ? (
              renderSubgroupVast(creative, creativeIdx)
            ) : (
              <Grid item className={classes.creativeRow}>
                <Grid container direction='column' justify='flex-start' alignItems='flex-start'>
                  {renderSubgroupMediafiles(creative, creativeIdx)}
                </Grid>
              </Grid>
            )}
          </Grid>
        );
      })}
      <div className={classes.section}>
        <Button
          style={{
            color: writeAuthorization.isAuthorized ? publicaColors.blue : publicaColors.lightGray,
          }}
          classes={{ root: classes.button }}
          onClick={addNewSubgroupCreative}
          disabled={!writeAuthorization.isAuthorized}
        >
          + Add New Creative
        </Button>
      </div>
    </Grid>
  );
};
